import type {AxiosError} from "axios";

import {isShiftOrBreakHttpPath} from "@/src/modules/core/lib/glitchtip/http-context";
import {
  isShiftDomainErrorCode,
  resolveErrorCode,
  SHIFT_DOMAIN_ERROR_CODES,
} from "@/src/modules/core/lib/glitchtip/error-codes";

const BREAK_ACTION_ERROR_TITLES: Record<string, string> = {
  [SHIFT_DOMAIN_ERROR_CODES.BREAK_BEFORE_SCHEDULED_TIME]:
    "Break cannot be started before its scheduled time.",
  [SHIFT_DOMAIN_ERROR_CODES.BREAK_EXTENDS_PAST_SHIFT_END]:
    "Break cannot extend past shift end.",
  [SHIFT_DOMAIN_ERROR_CODES.NO_ACTIVE_BREAK]: "No active break to end.",
};

function asAxiosError(
  error: unknown,
): AxiosError<{message?: string; error_code?: string}> | null {
  if (!error || typeof error !== "object" || !("isAxiosError" in error)) return null;
  return error as AxiosError<{message?: string; error_code?: string}>;
}

/** Expected business rejection on POST /break or shift/* (4xx). */
export function isBreakBusinessError(error: unknown): boolean {
  const axiosError = asAxiosError(error);
  if (!axiosError) return false;

  const status = axiosError.response?.status;
  if (typeof status !== "number" || status < 400 || status >= 500) return false;

  if (isShiftOrBreakHttpPath(axiosError.config?.url)) return true;

  return isShiftDomainErrorCode(resolveErrorCode(axiosError));
}

/** User-facing copy for break modal / card (maps known API messages; falls back to API `message`). */
export function getBreakActionErrorTitle(error: unknown): string {
  const axiosError = asAxiosError(error);
  if (!axiosError) return "Break action could not be completed.";

  const code = resolveErrorCode(axiosError);
  const mapped = BREAK_ACTION_ERROR_TITLES[code];
  if (mapped) return mapped;

  const apiMessage = axiosError.response?.data?.message?.trim();
  if (apiMessage) return apiMessage;

  return "Break action could not be completed.";
}
